import { useNavigate, useParams } from 'react-router-dom';
import useGetLists from '../hooks/useGetLists';
import { useEffect, useState } from 'react';
import { ArrowLeft, MoreVertical, Pen, Plus, Send, Trash } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import ListRenameModal from '../components/ListRenameModal';
import {
	DropdownMenu,
	DropdownMenuTrigger,
	DropdownMenuContent,
	DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import WishCard from '@/features/Wish/components/WishCard';
import useGetWishes from '@/features/Wish/hooks/useGetWishes';
import WishDetail from '@/features/Wish/components/WishDetail';
import ListShareModal from '../components/ListShareModal';
import ListEmpty from '../components/ListEmpty';
import ListDeleteModal from '../components/ListDeleteModal';

const List = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id_list } = useParams();

	const { lists } = useGetLists();
	const { wishes } = useGetWishes(parseInt(id_list as string));

	const [openRenameModal, setOpenRenameModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);

	useEffect(() => {
		if (!id_list || isNaN(parseInt(id_list))) {
			navigate('/lists');
		}
	}, [id_list, navigate]);

	if (!id_list) return null;

	const list = lists.find((list) => list.id === parseInt(id_list));
	if (!list) return null;

	const renderDropdown = () => {
		return (
			<DropdownMenu modal={false}>
				<DropdownMenuTrigger asChild>
					<MoreVertical className="cursor-pointer" size={20} />
				</DropdownMenuTrigger>
				<DropdownMenuContent>
					<DropdownMenuItem onClick={() => setOpenRenameModal(true)}>
						<Pen className="mr-2 h-4 w-4" />
						<span>{t('common.rename')}</span>
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => setOpenDeleteModal(true)}
						disabled={lists.length === 1}
					>
						<Trash className="mr-2 h-4 w-4" />
						<span>{t('common.delete')}</span>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		);
	};

	return (
		<div className="w-full">
			<div className="flex justify-between">
				<div className="flex items-center">
					<ArrowLeft
						className="mr-2 cursor-pointer"
						onClick={() => navigate('/home')}
					/>
				</div>
				{wishes && wishes?.length > 0 && (
					<div className="flex flex-row gap-3">
						<Button
							variant="outline"
							className="flex items-center"
							onClick={() => navigate(`/list/${id_list}/add`)}
						>
							<Plus className="mr-2 h-4 w-4" />
							{t('list.addWish')}
						</Button>
						<div className="hidden sm:block">
							<ListShareModal
								list={list}
								trigger={
									<Button
										variant="default"
										className="w-full"
									>
										<Send className="mr-2 h-4 w-4" />
										{t('list.share.action')}
									</Button>
								}
							/>
						</div>
					</div>
				)}
			</div>
			<div className="mt-4 flex flex-col">
				<span className="text-2xl font-bold">
					{list?.name}
					<span className="inline-flex items-center gap-2 whitespace-nowrap">
						&#xfeff;
						<span className="inline-block">{renderDropdown()}</span>
					</span>
				</span>
			</div>
			<div className="mt-4 flex flex-col gap-3 pb-24">
				{wishes?.map((wish) => (
					<WishCard
						key={wish.id}
						onClick={() => navigate(`/list/${id_list}/${wish.id}`)}
						wish={wish}
						list={list}
					/>
				))}
				{wishes?.length === 0 && <ListEmpty />}
			</div>

			<WishDetail />

			<div className="fixed bottom-0 left-0 w-full border-t border-secondary bg-background px-4 py-4 sm:hidden">
				<ListShareModal
					list={list}
					trigger={
						<Button size="lg" variant="default" className="w-full">
							<Send className="mr-2 h-4 w-4" />
							{t('list.share.action')}
						</Button>
					}
				/>
			</div>

			<ListRenameModal
				open={openRenameModal}
				setOpen={setOpenRenameModal}
			/>

			<ListDeleteModal
				open={openDeleteModal}
				setOpen={setOpenDeleteModal}
			/>
		</div>
	);
};

export default List;
